import { useClerk } from "@clerk/clerk-react";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Button, Surface, Text, useToast } from "@/shared/ui";
import { deleteAccount } from "../api/deleteAccount";

interface DeleteAccountDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Irreversible account deletion: a type-to-confirm modal (the user must type the
 * confirmation word) over the standard scrim + paper card. On success it signs
 * out and redirects home — the server has already deleted the account, so the
 * session is dead. Monochrome, like ConfirmDialog: the confirm button is the
 * strongest ink CTA, gated until the typed word matches.
 */
export function DeleteAccountDialog({ open, onClose }: DeleteAccountDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { signOut } = useClerk();
  const inputId = useId();
  const [typed, setTyped] = useState("");

  const word = t("settings.account.deleteConfirmWord");
  const matches = typed.trim() === word;

  const mutation = useMutation({
    mutationFn: deleteAccount,
    onSuccess: () => {
      // The account is gone server-side; clear the dead session and leave.
      void signOut({ redirectUrl: "/" });
    },
    onError: () => toast(t("settings.account.deleteError")),
  });

  const busy = mutation.isPending || mutation.isSuccess;

  // Reset the typed word whenever the dialog opens.
  useEffect(() => {
    if (open) setTyped("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape" && !busy) onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-label={t("settings.account.deleteDialogTitle")}
    >
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        onClick={() => !busy && onClose()}
        className="absolute inset-0 cursor-default bg-ink/30 backdrop-blur-[2px]"
      />
      <Surface
        tone="paper"
        radius="lg"
        border="hairline"
        shadow="pill"
        className="relative flex w-full max-w-[440px] flex-col gap-3 p-6"
      >
        <Text variant="h4" as="h2">
          {t("settings.account.deleteDialogTitle")}
        </Text>
        <Text variant="body" className="text-ink-soft">
          {t("settings.account.deleteDialogBody")}
        </Text>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (matches && !busy) mutation.mutate();
          }}
          className="mt-1 flex flex-col gap-2"
        >
          <label htmlFor={inputId} className="font-sans text-[13px] text-ink-soft">
            {t("settings.account.deleteConfirmLabel", { word })}
          </label>
          <input
            id={inputId}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            disabled={busy}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            aria-label={t("settings.account.deleteConfirmLabel", { word })}
            className="w-full rounded-md bg-bubble px-3 py-2 font-sans text-[14px] text-ink outline-none transition-colors duration-150 ease-house placeholder:text-ink-faint hover:bg-tint focus-visible:ring-[2px] focus-visible:ring-[var(--focus-ring)]"
          />

          <div className="mt-3 flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={busy}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" variant="danger" size="sm" disabled={!matches || busy}>
              {t("settings.account.deleteConfirmCta")}
            </Button>
          </div>
        </form>
      </Surface>
    </div>,
    document.body,
  );
}
